/**
 * CategoryAutocomplete — Autocomplete bilingüe de categorías GMB
 * (T-CATALOG opción C).
 *
 * Reemplaza el input libre de "tipo de negocio" del Modo Inteligente por
 * un combobox con búsqueda instantánea sobre el catálogo oficial GMB.
 * Implementa el Principio 1 del plan 4-capas (exactitud bilingüe en
 * categorías): el frontend muestra `label` ya resuelto al idioma del
 * usuario, pero el `onChange` emite SIEMPRE `id` oficial GMB para que
 * el backend trabaje con la taxonomía real (cero invención).
 *
 * Características:
 *   - Multi-selección con chips removibles (clic en × o backspace cuando
 *     el input está vacío).
 *   - Debounce 250ms para no saturar el endpoint.
 *   - Soporta `value` con ids cuyos label aún no están en cache: hace
 *     `getCategory(id, lang)` con cache local en memoria.
 *   - Estados loading/error/empty diferenciados.
 *   - A11y: role=combobox, aria-expanded, aria-activedescendant,
 *     aria-autocomplete=list. Soporta navegación con teclas
 *     ↑/↓/Enter/Escape/Backspace.
 *
 * NO depende del backend para funcionar — `categoriesService` cae al
 * mock cuando el endpoint no responde, y la UI sigue usable.
 *
 * **T-BLS-UI-FIX-V2 (2026-05-05 tarde)** (decisión F V2): el listbox
 * de resultados se renderiza vía `PortalDropdown` en `document.body`
 * para escapar del `overflow-hidden` del card padre
 * `MapsCreateIntelligentTab`. Sin esto, escribir "abog" mostraba sólo
 * la primera categoría visible. El click-outside antiguo
 * (`window.addEventListener('mousedown')` en este mismo archivo) ahora
 * lo gestiona el PortalDropdown (decisión D V2 portal-aware) — se
 * elimina el listener manual para evitar duplicación.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react'
import { Search, X, Loader2, AlertCircle, Database } from 'lucide-react'
import {
  searchCategories,
  getCategory,
  CategoriesError,
  type CategoryItem,
  type CategoryLang,
} from '@/services/categoriesService'
import { PortalDropdown } from '@/components/PortalDropdown'

// ============================================================================
// PROPS
// ============================================================================

export interface CategoryAutocompleteProps {
  /** Array de category_id ya seleccionados. Es la fuente de verdad. */
  value: string[]
  /** Callback cuando el usuario añade/quita un id. Recibe el array completo. */
  onChange: (ids: string[]) => void
  /** Idioma de display y de búsqueda (ES/EN). Se detecta con `detectUserLang()` arriba. */
  lang?: CategoryLang
  /** Filtro por país (ISO-3166-1 alpha-2). Se pasa al endpoint. */
  country?: string
  /** Texto del placeholder cuando no hay nada escrito. */
  placeholder?: string
  /** Tope de selecciones simultáneas. Default 10. */
  maxSelections?: number
  /** id HTML del input. Útil para `<label htmlFor>`. Default autogenerado. */
  id?: string
  /** Marca el input como inválido (para borde rojo). */
  hasError?: boolean
  /** Mensaje de ayuda inline debajo del componente (opcional). */
  helperText?: string
  /** z-index del desplegable (portal). Súbelo por encima del overlay de un
   *  modal que contenga este input (el modal de nueva extracción usa z-80). */
  dropdownZIndex?: number
}

// ============================================================================
// CACHE GLOBAL DE LOOKUPS INDIVIDUALES
// ============================================================================

/**
 * Cache simple en memoria para `getCategory(id, lang)`. Indexada por
 * `${id}|${lang}` → CategoryItem. Vive durante toda la sesión del
 * usuario; un reload la limpia. Si el catálogo se actualizara en caliente
 * (poco probable durante una sesión), basta con recargar.
 *
 * Diseño: cache por instancia (módulo) y no por componente, porque el
 * mismo id puede usarse en varios autocompletes (ej. en filtros futuros)
 * y revivimos el coste del round-trip si fuese por componente.
 */
const categoryCache = new Map<string, CategoryItem>()

function cacheKey(id: string, lang: CategoryLang): string {
  return `${id}|${lang}`
}

// ============================================================================
// COMPONENTE
// ============================================================================

export function CategoryAutocomplete({
  value,
  onChange,
  lang = 'es',
  country,
  placeholder,
  maxSelections = 10,
  id,
  hasError = false,
  helperText,
  dropdownZIndex,
}: CategoryAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const listboxId = useId()
  const fallbackInputId = useId()
  const inputId = id ?? `cat-autocomplete-${fallbackInputId}`

  // DEV-only: abrir el desplegable con una query semilla para el bucle de
  // fidelidad (capture.mjs no puede teclear/enfocar). `?catopen[&catq=...]`.
  const PREVIEW_OPEN =
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('catopen')
  const PREVIEW_Q =
    PREVIEW_OPEN
      ? new URLSearchParams(window.location.search).get('catq') || 'a'
      : ''

  const [query, setQuery] = useState(PREVIEW_Q)
  const [debouncedQuery, setDebouncedQuery] = useState(PREVIEW_Q)
  const [open, setOpen] = useState(PREVIEW_OPEN)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<{ message: string } | null>(null)
  const [results, setResults] = useState<CategoryItem[]>([])
  const [isFallback, setIsFallback] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(0)

  // Catálogo local para resolver labels de los `value` ya seleccionados.
  // Empieza vacío y se llena con (a) lo que devuelve search y (b) lo que
  // devuelve getCategory para ids huérfanos.
  const [resolvedItems, setResolvedItems] = useState<Record<string, CategoryItem>>({})

  // ---------------------------------------------------------------------------
  // DEBOUNCE
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query)
    }, 250)
    return () => window.clearTimeout(timer)
  }, [query])

  // ---------------------------------------------------------------------------
  // BÚSQUEDA REMOTA / MOCK
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!open) return
    let cancelled = false

    async function run() {
      setLoading(true)
      setError(null)
      try {
        const res = await searchCategories({
          q: debouncedQuery,
          lang,
          country,
          limit: 20,
        })
        if (cancelled) return
        setResults(res.data)
        setIsFallback(res.is_fallback)
        setHighlightIndex(0)

        // Aprovechamos para meter en el cache de lookup individual los
        // items que aparecen en search — evita un round-trip si luego el
        // usuario añade ese id por otra vía.
        for (const item of res.data) {
          categoryCache.set(cacheKey(item.id, lang), item)
        }
      } catch (e) {
        if (cancelled) return
        if (e instanceof CategoriesError) {
          setError({ message: e.message })
        } else {
          setError({
            message: e instanceof Error ? e.message : 'Error al buscar categorías.',
          })
        }
        setResults([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [open, debouncedQuery, lang, country])

  // ---------------------------------------------------------------------------
  // RESOLUCIÓN DE LABELS DE `value`
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false
    const idsToResolve = value.filter(id => !resolvedItems[id])
    if (idsToResolve.length === 0) return

    async function resolveAll() {
      const fresh: Record<string, CategoryItem> = {}
      for (const id of idsToResolve) {
        const cached = categoryCache.get(cacheKey(id, lang))
        if (cached) {
          fresh[id] = cached
          continue
        }
        try {
          const res = await getCategory(id, lang)
          if (cancelled) return
          if (res.data) {
            fresh[id] = res.data
            categoryCache.set(cacheKey(id, lang), res.data)
          } else {
            // id desconocido — guardamos un placeholder para no re-pedirlo.
            fresh[id] = {
              id,
              label: id,
              aliases: [],
              sector: 'unknown',
            }
          }
        } catch {
          // Errores de auth no llegan aquí porque getCategory cae al mock
          // antes; si falla por otro motivo, dejamos el id sin label
          // (placeholder con el propio id) y seguimos.
          fresh[id] = {
            id,
            label: id,
            aliases: [],
            sector: 'unknown',
          }
        }
      }
      if (!cancelled && Object.keys(fresh).length > 0) {
        setResolvedItems(prev => ({ ...prev, ...fresh }))
      }
    }

    resolveAll()
    return () => {
      cancelled = true
    }
  }, [value, lang, resolvedItems])

  // T-BLS-UI-FIX-V2 decisión D V2: el click-outside lo gestiona ahora
  // `PortalDropdown` (portal-aware: distingue clicks dentro del anchor
  // y dentro del propio portal). Eliminado el listener manual.

  // ---------------------------------------------------------------------------
  // ACCIONES
  // ---------------------------------------------------------------------------

  const selectedItems = useMemo<CategoryItem[]>(
    () =>
      value.map(
        id =>
          resolvedItems[id] ?? {
            id,
            label: id,
            aliases: [],
            sector: 'unknown',
          },
      ),
    [value, resolvedItems],
  )

  const filteredResults = useMemo(
    () => results.filter(r => !value.includes(r.id)),
    [results, value],
  )

  const atMax = value.length >= maxSelections

  const addCategory = useCallback(
    (item: CategoryItem) => {
      if (atMax) return
      if (value.includes(item.id)) return
      onChange([...value, item.id])
      setResolvedItems(prev => ({ ...prev, [item.id]: item }))
      categoryCache.set(cacheKey(item.id, lang), item)
      setQuery('')
      setDebouncedQuery('')
      // Mantener foco en el input para añadir más
      inputRef.current?.focus()
    },
    [atMax, lang, onChange, value],
  )

  const removeCategory = useCallback(
    (id: string) => {
      onChange(value.filter(v => v !== id))
      inputRef.current?.focus()
    },
    [onChange, value],
  )

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && query.length === 0 && value.length > 0) {
      // Quitar la última seleccionada
      e.preventDefault()
      removeCategory(value[value.length - 1])
      return
    }
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault()
        setOpen(true)
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex(i => Math.min(i + 1, Math.max(0, filteredResults.length - 1)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const item = filteredResults[highlightIndex]
      if (item) addCategory(item)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
    }
  }

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  const placeholderText =
    placeholder ??
    (lang === 'en'
      ? 'Search for a business category (e.g. tax consultant, hair salon)…'
      : 'Buscar categoría de negocio (ej. gestoría laboral, peluquería)…')

  const borderClass = hasError
    ? 'border-rose-400 focus-within:ring-rose-300'
    : 'border-gray-200 focus-within:ring-teal-400'

  return (
    <div ref={containerRef} className="relative">
      {/* Input + chips */}
      <div
        className={`flex flex-wrap items-center gap-1.5 px-3 py-1.5 min-h-[42px] border rounded-lg bg-white focus-within:outline-none focus-within:ring-2 transition-shadow ${borderClass}`}
        onClick={() => {
          inputRef.current?.focus()
          setOpen(true)
        }}
      >
        <Search size={15} className="text-gray-400 flex-shrink-0" aria-hidden="true" />

        {selectedItems.map(item => (
          <span
            key={item.id}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-50 border border-teal-200 text-teal-800 text-xs font-medium"
          >
            <span className="truncate max-w-[180px]">{item.label}</span>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                removeCategory(item.id)
              }}
              className="text-teal-700 hover:text-teal-900 focus:outline-none focus:ring-1 focus:ring-teal-500 rounded"
              aria-label={
                lang === 'en' ? `Remove ${item.label}` : `Quitar ${item.label}`
              }
            >
              <X size={12} />
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          id={inputId}
          type="text"
          value={query}
          onChange={e => {
            setQuery(e.target.value)
            if (!open) setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholderText : ''}
          disabled={atMax}
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && filteredResults[highlightIndex]
              ? `${listboxId}-opt-${filteredResults[highlightIndex].id}`
              : undefined
          }
          className="flex-1 min-w-[140px] bg-transparent text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed disabled:text-gray-400"
        />
      </div>

      {/* Helper text inline (cuando se llega al máximo, sustituye al helper externo) */}
      {atMax && (
        <p className="mt-1 text-xs text-amber-600">
          {lang === 'en'
            ? `Maximum of ${maxSelections} categories reached.`
            : `Máximo de ${maxSelections} categorías alcanzado.`}
        </p>
      )}
      {!atMax && helperText && (
        <p className="mt-1 text-xs text-gray-400">{helperText}</p>
      )}

      {/* Dropdown — T-BLS-UI-FIX-V2: portal a document.body para
          escapar overflow-hidden del card padre. Decisión F V2. */}
      <PortalDropdown
        anchorRef={containerRef}
        isOpen={open}
        onClose={() => setOpen(false)}
        zIndex={dropdownZIndex}
        // `cabina-form`: el portal vive en document.body (fuera del form), así que
        // sin esta clase el scope de tema (T-026) no lo alcanza y en oscuro saldría
        // blanco. Con ella, los tokens cabina aplican al desplegable (claro+oscuro).
        className="cabina-form bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
      >
        <div
          id={listboxId}
          role="listbox"
          className="max-h-72 overflow-y-auto"
        >
          {/* Estado: cargando */}
          {loading && (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-gray-500">
              <Loader2 size={14} className="animate-spin text-teal-500" />
              <span>{lang === 'en' ? 'Searching…' : 'Buscando…'}</span>
            </div>
          )}

          {/* Estado: error funcional */}
          {!loading && error && (
            <div className="flex items-start gap-2 px-3 py-3 text-sm text-rose-700 bg-rose-50">
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
              <span>{error.message}</span>
            </div>
          )}

          {/* Banner fallback (mock) */}
          {!loading && !error && isFallback && filteredResults.length > 0 && (
            <div className="flex items-start gap-2 px-3 py-2 text-xs text-blue-800 bg-blue-50 border-b border-blue-100">
              <Database size={12} className="flex-shrink-0 mt-0.5" />
              <span>
                {lang === 'en'
                  ? 'Demo data — backend unavailable.'
                  : 'Datos de demostración — backend no disponible.'}
              </span>
            </div>
          )}

          {/* Estado: empty */}
          {!loading && !error && filteredResults.length === 0 && (
            <div className="px-3 py-4 text-sm text-gray-500 text-center">
              {lang === 'en'
                ? "No categories match. Try another keyword."
                : 'No encontramos categorías que coincidan, intenta con otra palabra.'}
            </div>
          )}

          {/* Resultados */}
          {!loading && !error && filteredResults.length > 0 && (
            <ul className="py-1">
              {filteredResults.map((item, idx) => {
                const isHighlighted = idx === highlightIndex
                return (
                  <li
                    key={item.id}
                    id={`${listboxId}-opt-${item.id}`}
                    role="option"
                    aria-selected={isHighlighted}
                    onMouseDown={e => {
                      // mousedown (no click) para evitar que el blur del
                      // input cierre el dropdown antes del click.
                      e.preventDefault()
                      addCategory(item)
                    }}
                    onMouseEnter={() => setHighlightIndex(idx)}
                    className={`px-3 py-2 cursor-pointer flex items-baseline justify-between gap-3 ${
                      isHighlighted ? 'bg-teal-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-800 truncate">
                        {item.label}
                      </div>
                      {item.aliases.length > 0 && (
                        <div className="text-xs text-gray-400 truncate">
                          {item.aliases.slice(0, 4).join(', ')}
                        </div>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-400 font-mono flex-shrink-0">
                      {item.id}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </PortalDropdown>
    </div>
  )
}
